import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, Plus, Trash, Truck } from "lucide-react";

const emptyLine = () => ({ _key: crypto.randomUUID(), sku: "", expected_quantity: "", note: "" });

export default function NewExpectedDeliveryDialog({ open, onOpenChange, onSuccess, defaultProjectId }) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(defaultProjectId || "");
  const [reference, setReference] = useState("");
  const [vendor, setVendor] = useState("");
  const [deliveryLocation, setDeliveryLocation] = useState("");
  const [expectedAt, setExpectedAt] = useState("");
  const [note, setNote] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [lines, setLines] = useState([emptyLine()]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setReference("");
      setVendor("");
      setDeliveryLocation("");
      setExpectedAt("");
      setNote("");
      setLines([emptyLine()]);
      setProjectId(defaultProjectId || "");
      Promise.all([
        api.get("/projects", { params: { archived: false } }),
        api.get("/inventory/items"),
      ]).then(([p, c]) => {
        setProjects(p.data);
        setCatalog(c.data);
      });
    }
  }, [open, defaultProjectId]);

  const updateLine = (i, field, value) =>
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const removeLine = (i) => setLines((ls) => ls.filter((_, idx) => idx !== i));

  const validLines = lines.filter((l) => l.sku.trim() && Number(l.expected_quantity) > 0);
  const total = validLines.reduce((a, l) => a + Number(l.expected_quantity || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!projectId) return toast.error("Select a project");
    if (validLines.length === 0) return toast.error("Add at least one line with SKU and quantity");
    setBusy(true);
    try {
      await api.post("/expected-deliveries", {
        project_id: projectId,
        reference: reference.trim(),
        vendor: vendor.trim(),
        delivery_location: deliveryLocation.trim(),
        expected_at: expectedAt || null,
        note: note.trim(),
        lines: validLines.map((l) => ({
          sku: l.sku.trim(),
          expected_quantity: Number(l.expected_quantity),
          note: l.note || "",
        })),
      });
      toast.success("Expected delivery scheduled");
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create expected delivery");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl rounded-md" data-testid="new-expected-delivery-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="grid place-items-center h-10 w-10 rounded-sm bg-indigo-100 text-indigo-800">
              <Truck size={20} />
            </div>
            <div>
              <DialogTitle className="font-display text-2xl font-black tracking-tight">
                New Off-Site Delivery
              </DialogTitle>
              <DialogDescription className="text-slate-600 mt-1">
                Manually pre-load a delivery going straight to a job site. Once saved it becomes an
                <span className="font-semibold"> Open </span> delivery you can tap and receive when the truck arrives.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1">
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Project</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger data-testid="expected-project-select" className="rounded-sm mt-1.5">
                  <SelectValue placeholder="Choose a project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Reference / PO</Label>
              <Input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="PO-8821"
                data-testid="expected-reference-input"
                className="rounded-sm mt-1.5 font-mono"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Expected Date</Label>
              <Input
                type="date"
                value={expectedAt ? expectedAt.slice(0, 10) : ""}
                onChange={(e) => setExpectedAt(e.target.value ? new Date(e.target.value).toISOString() : "")}
                data-testid="expected-date-input"
                className="rounded-sm mt-1.5"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Vendor</Label>
              <Input
                value={vendor}
                onChange={(e) => setVendor(e.target.value)}
                placeholder="e.g. Acme Furniture Co"
                data-testid="expected-vendor-input"
                className="rounded-sm mt-1.5"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Delivery Location</Label>
              <Input
                value={deliveryLocation}
                onChange={(e) => setDeliveryLocation(e.target.value)}
                placeholder="Job site address"
                data-testid="expected-location-input"
                className="rounded-sm mt-1.5"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Note</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Carrier, driver, notes"
              className="rounded-sm mt-1.5"
            />
          </div>

          <div className="border border-slate-200 rounded-md overflow-hidden">
            <div className="flex items-center justify-between bg-slate-50 border-b border-slate-200 p-3">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Line Items</div>
              <Button
                type="button"
                onClick={addLine}
                variant="outline"
                size="sm"
                data-testid="expected-add-line-btn"
                className="rounded-sm border-slate-300"
              >
                <Plus size={14} className="mr-1.5" /> Add Line
              </Button>
            </div>
            <div className="divide-y divide-slate-200 max-h-[45vh] overflow-y-auto">
              {lines.map((l, i) => (
                <div key={l._key} className="p-3 grid grid-cols-12 gap-2 items-end" data-testid={`expected-line-${i}`}>
                  <div className="col-span-5">
                    {i === 0 && <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">SKU Code</Label>}
                    <Input
                      value={l.sku}
                      onChange={(e) => updateLine(i, "sku", e.target.value)}
                      placeholder="e.g. 105 or HW-1002"
                      list={`sku-datalist-exp-${i}`}
                      data-testid={`expected-sku-${i}`}
                      className="rounded-sm mt-1 font-mono uppercase"
                    />
                    <datalist id={`sku-datalist-exp-${i}`}>
                      {catalog.map((c) => (
                        <option key={c.id} value={c.sku}>{c.name}</option>
                      ))}
                    </datalist>
                  </div>
                  <div className="col-span-3">
                    {i === 0 && <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Expected Qty</Label>}
                    <Input
                      type="number"
                      min="1"
                      value={l.expected_quantity}
                      onChange={(e) => updateLine(i, "expected_quantity", e.target.value)}
                      data-testid={`expected-qty-${i}`}
                      className="rounded-sm mt-1 font-mono"
                    />
                  </div>
                  <div className="col-span-3">
                    {i === 0 && <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Line Note</Label>}
                    <Input
                      value={l.note}
                      onChange={(e) => updateLine(i, "note", e.target.value)}
                      placeholder="optional"
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeLine(i)}
                      disabled={lines.length === 1}
                      aria-label="Remove line"
                      data-testid={`expected-remove-line-${i}`}
                      className="h-9 w-9 rounded-sm text-slate-500 hover:text-red-600"
                    >
                      <Trash size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {validLines.length > 0 && (
            <div className="text-sm text-slate-600">
              <span className="font-mono font-semibold text-slate-900">{total}</span> units across {validLines.length} SKU{validLines.length === 1 ? "" : "s"} scheduled.
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button
              type="submit"
              disabled={busy}
              data-testid="expected-submit-btn"
              className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm"
            >
              {busy && <Loader2 size={16} className="animate-spin mr-1.5" />}
              Schedule Delivery
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
